// frontend/src/pages/admin/AdminFinance.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { financeApi } from '../../api/finance';
import helpers from '../../utils/helpers';
import { 
    FaMoneyBillWave, FaExchangeAlt, FaSpinner, FaArrowUp, 
    FaFileInvoiceDollar, FaBox, FaTrash, FaDownload, FaSearch, FaSort, FaEye, FaCreditCard, FaTimes,
    FaUniversity, FaCalendarCheck, FaMapMarkerAlt, FaListAlt, FaUserShield 
} from 'react-icons/fa';

function AdminFinance() {
  const [stats, setStats] = useState({ total_revenue: 0, pending_revenue: 0 });
  const [transactions, setTransactions] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]); // <--- NEW STATE
  const [loading, setLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState('transactions');

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); 
  const [filterStatus, setFilterStatus] = useState('all'); 
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const [selectedItems, setSelectedItems] = useState([]); 
  const [isDeleting, setIsDeleting] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const [settlementPage, setSettlementPage] = useState(1);
  const [settlementMeta, setSettlementMeta] = useState({ total: 0, page: 1, size: 20 });
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchSettlements = useCallback(async () => {
      setLoadingSettlements(true);
      try {
          const response = await financeApi.getSettlements(settlementPage, settlementMeta.size);
          setSettlements(response.data || []);
          setSettlementMeta(response.meta || { total: 0, page: 1, size: 20 });
      } catch (err) {
          console.error("Failed to load settlements", err);
          setSettlements([]);
      } finally {
          setLoadingSettlements(false);
      }
  }, [settlementPage, settlementMeta.size]);

  // --- NEW: FETCH AUDIT LOGS ---
  const fetchAuditLogs = useCallback(async () => {
      try {
          const response = await financeApi.getAuditTrail();
          setAuditLogs(response || []);
      } catch (err) {
          console.error("Failed to load audit logs", err);
      }
  }, []);

  useEffect(() => {
      if (activeTab === 'settlements') {
          fetchSettlements();
      } else if (activeTab === 'audit') {
          fetchAuditLogs();
      }
  }, [activeTab, fetchSettlements, fetchAuditLogs]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, txData] = await Promise.all([
        financeApi.getStats(),
        financeApi.getTransactions()
      ]);
      setStats(statsData);
      setTransactions(txData);
    } catch (err) {
      console.error("Failed to load finance data", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
            tx.reference.toLowerCase().includes(searchLower) ||
            tx.customer.toLowerCase().includes(searchLower) ||
            tx.amount.toString().includes(searchLower);

        const matchesType = filterType === 'all' || tx.type === filterType;

        let matchesStatus = true;
        if (filterStatus === 'settled') {
            matchesStatus = ['paid', 'delivered', 'completed'].includes(tx.status.toLowerCase());
        } else if (filterStatus === 'pending') {
            matchesStatus = ['pending', 'processing', 'shipped', 'unpaid'].includes(tx.status.toLowerCase());
        }

        let matchesDate = true;
        if (dateRange.start) {
            matchesDate = matchesDate && new Date(tx.date) >= new Date(dateRange.start);
        }
        if (dateRange.end) {
            const endDate = new Date(dateRange.end);
            endDate.setDate(endDate.getDate() + 1);
            matchesDate = matchesDate && new Date(tx.date) < endDate;
        }

        return matchesSearch && matchesType && matchesStatus && matchesDate;
    }).sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
  }, [transactions, searchTerm, filterType, filterStatus, dateRange, sortConfig]);

  const handleSort = (key) => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
          direction = 'desc';
      }
      setSortConfig({ key, direction });
  };

  const handleSelectAll = (e) => {
      if (e.target.checked) {
          const allItems = filteredTransactions.map(tx => ({ id: tx.id, source: tx.source }));
          setSelectedItems(allItems);
      } else {
          setSelectedItems([]);
      }
  };

  const handleSelectOne = (id, source) => {
      setSelectedItems(prev => {
          const exists = prev.find(item => item.id === id);
          if (exists) {
              return prev.filter(item => item.id !== id);
          } else {
              return [...prev, { id, source }];
          }
      });
  };

  const handleDeleteSelected = async () => {
      if (selectedItems.length === 0) return;
      
      if (!window.confirm(`⚠️ WARNING: You are about to delete ${selectedItems.length} financial records. This action cannot be undone. Are you sure?`)) {
          return;
      }

      setIsDeleting(true);
      try {
          await financeApi.deleteTransactions(selectedItems);
          alert("Records deleted successfully.");
          setSelectedItems([]);
          fetchData();
      } catch (err) {
          alert("Failed to delete records.");
      } finally {
          setIsDeleting(false);
      }
  };

  const handleExportCSV = () => {
      const headers = ["Date", "Reference", "Type", "Customer", "Amount", "Status", "Payment Method"];
      const rows = filteredTransactions.map(tx => [
          new Date(tx.date).toLocaleDateString(),
          tx.reference,
          tx.type,
          tx.customer,
          tx.amount,
          tx.status,
          tx.payment_method
      ]);

      const csvContent = "data:text/csv;charset=utf-8," 
          + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `financial_report_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const toggleStatusFilter = (status) => {
      if (filterStatus === status) {
          setFilterStatus('all'); 
      } else {
          setFilterStatus(status); 
      }
  };

  const clearAllFilters = () => {
      setSearchTerm('');
      setFilterType('all');
      setFilterStatus('all');
      setDateRange({ start: '', end: '' });
  };

  const hasActiveFilters = searchTerm || filterType !== 'all' || filterStatus !== 'all' || dateRange.start || dateRange.end;

  const getStatusBadge = (status) => {
    const s = status.toLowerCase();
    if (['paid', 'delivered', 'completed', 'successful'].includes(s)) {
      return <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-bold uppercase">Settled</span>;
    } else if (['pending', 'processing', 'shipped', 'unpaid'].includes(s)) {
      return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded font-bold uppercase">Pending</span>;
    } else {
      return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded font-bold uppercase">{status}</span>;
    }
  };

  const getTypeIcon = (type) => {
      if (type === 'Product Sale') return <FaBox className="text-blue-500" />;
      return <FaFileInvoiceDollar className="text-purple-500" />;
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Financial Data...</div>;

  return (
    <div className="pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaMoneyBillWave className="text-green-600" /> Financials
          </h1>
          <div className="w-full sm:w-auto">
              <button 
                  onClick={handleExportCSV}
                  className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded font-bold flex items-center justify-center gap-2 hover:bg-blue-700 shadow-sm"
              >
                  <FaDownload /> Export CSV
              </button>
          </div>
      </div>

      <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto">
          <button 
              onClick={() => setActiveTab('transactions')}
              className={`pb-2 px-4 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'transactions' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
          >
              <FaExchangeAlt /> Transactions Ledger
          </button>
          <button 
              onClick={() => setActiveTab('settlements')}
              className={`pb-2 px-4 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'settlements' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
          >
              <FaUniversity /> Bank Settlements
          </button>
          {/* --- NEW TAB --- */}
          <button 
              onClick={() => setActiveTab('audit')}
              className={`pb-2 px-4 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'audit' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
          >
              <FaListAlt /> Credit Audit Trail
          </button>
      </div>

      {activeTab === 'transactions' && (
          <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div 
                    onClick={() => toggleStatusFilter('settled')}
                    className={`bg-white p-5 rounded-lg shadow-sm border-l-4 border-green-500 flex justify-between items-center cursor-pointer transition-all hover:shadow-md ${filterStatus === 'settled' ? 'ring-2 ring-green-500 bg-green-50' : ''}`}
                >
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase mb-1">Total Revenue (Settled)</p>
                    <h3 className="text-2xl font-bold text-gray-800">{helpers.formatCurrency(stats.total_revenue, 'NGN')}</h3>
                  </div>
                  <div className="p-3 rounded-full bg-green-50 text-green-500">
                    <FaArrowUp />
                  </div>
                </div>

                <div 
                    onClick={() => toggleStatusFilter('pending')}
                    className={`bg-white p-5 rounded-lg shadow-sm border-l-4 border-yellow-500 flex justify-between items-center cursor-pointer transition-all hover:shadow-md ${filterStatus === 'pending' ? 'ring-2 ring-yellow-500 bg-yellow-50' : ''}`}
                >
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase mb-1">Pending Revenue</p>
                    <h3 className="text-2xl font-bold text-gray-800">{helpers.formatCurrency(stats.pending_revenue, 'NGN')}</h3>
                  </div>
                  <div className="p-3 rounded-full bg-yellow-50 text-yellow-500">
                    <FaExchangeAlt />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-sm mb-6 border border-gray-200">
                  <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                          <div className="relative col-span-1 sm:col-span-2 lg:col-span-1">
                              <FaSearch className="absolute left-3 top-3 text-gray-400" />
                              <input 
                                  type="text" 
                                  placeholder="Search Ref, Customer..." 
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  className="pl-10 pr-4 py-2 border rounded w-full focus:outline-none focus:border-primary"
                              />
                          </div>
                          
                          <select 
                              value={filterType} 
                              onChange={(e) => setFilterType(e.target.value)}
                              className="p-2 border rounded bg-white focus:outline-none focus:border-primary w-full"
                          >
                              <option value="all">All Types</option>
                              <option value="Product Sale">Product Sales</option>
                              <option value="Service Revenue">Service Revenue</option>
                          </select>

                          <select 
                              value={filterStatus} 
                              onChange={(e) => setFilterStatus(e.target.value)}
                              className="p-2 border rounded bg-white focus:outline-none focus:border-primary w-full"
                          >
                              <option value="all">All Statuses</option>
                              <option value="settled">Settled</option>
                              <option value="pending">Pending</option>
                          </select>

                          <div className="flex flex-col sm:flex-row gap-2 col-span-1 sm:col-span-2 lg:col-span-2">
                              <input 
                                  type="date" 
                                  value={dateRange.start}
                                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                                  className="p-2 border rounded text-sm w-full"
                                  placeholder="Start Date"
                              />
                              <input 
                                  type="date" 
                                  value={dateRange.end}
                                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                                  className="p-2 border rounded text-sm w-full"
                                  placeholder="End Date"
                              />
                          </div>
                      </div>

                      <div className="flex justify-between items-center pt-2 border-t border-gray-100 mt-2">
                          <div>
                              {hasActiveFilters && (
                                  <button 
                                      onClick={clearAllFilters}
                                      className="text-red-500 text-sm font-bold hover:text-red-700 flex items-center gap-1"
                                  >
                                      <FaTimes /> Clear Filters
                                  </button>
                              )}
                          </div>
                          
                          {selectedItems.length > 0 && (
                              <button 
                                  onClick={handleDeleteSelected}
                                  disabled={isDeleting}
                                  className="bg-red-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 hover:bg-red-700 shadow-sm transition-all"
                              >
                                  {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} 
                                  Delete Selected ({selectedItems.length})
                              </button>
                          )}
                      </div>
                  </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                        <th className="p-4 w-10">
                            <input 
                                type="checkbox" 
                                onChange={handleSelectAll}
                                checked={filteredTransactions.length > 0 && selectedItems.length === filteredTransactions.length}
                                className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                            />
                        </th>
                        <th className="p-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('date')}>
                            <div className="flex items-center gap-1">Date <FaSort /></div>
                        </th>
                        <th className="p-4">Reference</th>
                        <th className="p-4">Type</th>
                        <th className="p-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('customer')}>
                            <div className="flex items-center gap-1">Customer <FaSort /></div>
                        </th>
                        <th className="p-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('amount')}>
                            <div className="flex items-center gap-1">Amount <FaSort /></div>
                        </th>
                        <th className="p-4">Payment Method</th>
                        <th className="p-4">Status</th>
                        <th className="p-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="p-8 text-center text-gray-500">No transactions found matching filters.</td>
                        </tr>
                      ) : (
                        filteredTransactions.map((tx) => {
                            const isSelected = selectedItems.some(item => item.id === tx.id);
                            return (
                              <tr key={tx.id} className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                                <td className="p-4">
                                    <input 
                                        type="checkbox" 
                                        checked={isSelected}
                                        onChange={() => handleSelectOne(tx.id, tx.source)}
                                        className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                                    />
                                </td>
                                <td className="p-4 text-sm text-gray-600">
                                  {new Date(tx.date).toLocaleDateString()} <span className="text-xs text-gray-400 block">{new Date(tx.date).toLocaleTimeString()}</span>
                                </td>
                                <td className="p-4 font-mono text-sm font-bold text-primary">
                                    {tx.source === 'orders' ? (
                                        <Link to={`/admin/orders`} className="hover:underline">{tx.reference}</Link>
                                    ) : (
                                        <Link to={`/admin/services`} className="hover:underline">{tx.reference}</Link>
                                    )}
                                </td>
                                <td className="p-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        {getTypeIcon(tx.type)}
                                        {tx.type}
                                    </div>
                                </td>
                                <td className="p-4 text-sm text-gray-800 font-medium">
                                  {tx.customer}
                                </td>
                                <td className="p-4 text-sm font-bold text-gray-800">
                                  {helpers.formatCurrency(tx.amount, 'NGN')}
                                </td>
                                <td className="p-4 text-sm text-gray-600 capitalize">
                                    <div className="flex items-center gap-1">
                                        <FaCreditCard className="text-gray-400" />
                                        {tx.payment_method ? tx.payment_method.replace(/_/g, ' ') : 'N/A'}
                                    </div>
                                </td>
                                <td className="p-4">
                                  {getStatusBadge(tx.status)}
                                </td>
                                <td className="p-4 text-right">
                                    {tx.source === 'orders' ? (
                                        <Link to="/admin/orders" className="text-gray-400 hover:text-blue-600 p-2"><FaEye /></Link>
                                    ) : (
                                        <Link to="/admin/services" className="text-gray-400 hover:text-blue-600 p-2"><FaEye /></Link>
                                    )}
                                </td>
                              </tr>
                            );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
          </>
      )}

      {activeTab === 'settlements' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="p-4 bg-gray-50 border-b">
                  <p className="text-sm text-gray-600">
                      This data is pulled directly from Flutterwave's API and shows when collected funds are deposited into your bank account.
                  </p>
              </div>
              
              {loadingSettlements ? (
                  <div className="p-10 text-center">
                      <FaSpinner className="animate-spin text-2xl text-primary" /> Loading Settlements...
                  </div>
              ) : settlements.length === 0 ? (
                  <div className="p-10 text-center text-gray-500">
                      No settlements found. (Settlements only occur for Live/Production transactions).
                  </div>
              ) : (
                  <>
                      <div className="overflow-x-auto">
                          <table className="w-full text-left border-collapse min-w-[800px]">
                              <thead>
                                  <tr className="bg-gray-100 text-gray-600 text-sm uppercase tracking-wider border-b">
                                      <th className="p-4">Settlement Date</th>
                                      <th className="p-4">Gross Amount</th>
                                      <th className="p-4">Net Amount</th>
                                      <th className="p-4">Transactions</th>
                                      <th className="p-4">Destination</th>
                                      <th className="p-4">Status</th>
                                  </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                  {settlements.map(s => (
                                      <tr key={s.id} className="hover:bg-gray-50">
                                          <td className="p-4 text-sm text-gray-800">
                                              <div className="font-bold flex items-center gap-2"><FaCalendarCheck className="text-primary" /> {new Date(s.due_datetime).toLocaleDateString()}</div>
                                              <div className="text-xs text-gray-500">{new Date(s.due_datetime).toLocaleTimeString()}</div>
                                          </td>
                                          <td className="p-4 font-bold text-gray-800">{helpers.formatCurrency(s.gross_amount, s.currency)}</td>
                                          <td className="p-4 font-bold text-green-600">{helpers.formatCurrency(s.net_amount, s.currency)}</td>
                                          <td className="p-4 text-sm text-gray-600">{s.charge_count}</td>
                                          <td className="p-4 text-sm text-gray-600 flex items-center gap-2"><FaMapMarkerAlt /> {s.destination}</td>
                                          <td className="p-4">
                                              {getStatusBadge(s.status)}
                                          </td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      </div>
                      
                      <div className="p-4 border-t flex justify-between items-center">
                          <p className="text-sm text-gray-600">Showing {settlementMeta.page * settlementMeta.size - settlementMeta.size + 1} - {Math.min(settlementMeta.page * settlementMeta.size, settlementMeta.total)} of {settlementMeta.total}</p>
                          <div className="flex gap-2">
                              <button 
                                  onClick={() => setSettlementPage(prev => Math.max(1, prev - 1))}
                                  disabled={settlementPage === 1}
                                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                              >
                                  Previous
                              </button>
                              <button 
                                  onClick={() => setSettlementPage(prev => prev + 1)}
                                  disabled={settlementPage * settlementMeta.size >= settlementMeta.total}
                                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                              >
                                  Next
                              </button>
                          </div>
                      </div>
                  </>
              )}
          </div>
      )}

      {/* --- NEW: AUDIT TRAIL TAB --- */}
      {activeTab === 'audit' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="p-4 bg-gray-50 border-b">
                  <p className="text-sm text-gray-600">
                      Immutable record of all manual wallet credits issued by admins.
                  </p>
              </div>
              <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                      <thead>
                          <tr className="bg-gray-100 text-gray-600 text-sm uppercase tracking-wider border-b">
                              <th className="p-4">Date</th>
                              <th className="p-4">Admin (Sender)</th>
                              <th className="p-4">User (Receiver)</th>
                              <th className="p-4">Amount</th>
                              <th className="p-4">Reason</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                          {auditLogs.length === 0 ? (
                              <tr><td colSpan="5" className="p-8 text-center text-gray-500">No manual credits recorded.</td></tr>
                          ) : (
                              auditLogs.map(log => (
                                  <tr key={log.id} className="hover:bg-gray-50">
                                      <td className="p-4 text-sm text-gray-600">
                                          {new Date(log.timestamp).toLocaleString()}
                                      </td>
                                      <td className="p-4 text-sm">
                                          <div className="flex items-center gap-2">
                                              <FaUserShield className="text-red-500" />
                                              {log.admin_email}
                                          </div>
                                      </td>
                                      <td className="p-4 text-sm font-medium text-gray-800">
                                          {log.target_user_email}
                                      </td>
                                      <td className="p-4 font-bold text-green-600">
                                          {helpers.formatCurrency(log.amount, 'NGN')}
                                      </td>
                                      <td className="p-4 text-sm text-gray-600">
                                          {log.reason}
                                      </td>
                                  </tr>
                              ))
                          )}
                      </tbody>
                  </table>
              </div>
          </div>
      )}
    </div>
  );
}

export default AdminFinance;